import {
  type CservOnlineTools,
  cservOnlineTools,
  type ExperteNameWithGesperrtStatus,
  type LocationArt,
  type SelectedExperte,
} from '../../dtos';
import { content } from '../content';
import { unique, uniqueBy } from '../unique';

type GetStandortVa<T> = {
  standortBlockungen: {
    standort: T;
  }[];
};

function getStandort<T>(veranstaltung: GetStandortVa<T>): T | undefined {
  return veranstaltung.standortBlockungen.at(0)?.standort;
}

type GetLocationVa<R> = {
  raumBlockungen: {
    raum: {
      location: R;
    };
  }[];
};

function getLocation<T>(veranstaltung: GetLocationVa<T>): T | undefined {
  return veranstaltung.raumBlockungen.at(0)?.raum.location;
}

type GetLocationNameVa = {
  onlineTool: CservOnlineTools | null;
  standortBlockungen: {
    standort: {
      name: string;
    };
  }[];
  raumBlockungen: {
    raum: {
      location: {
        name: string;
      };
    };
  }[];
};

export const getLocationName = (veranstaltung: GetLocationNameVa): string | undefined => {
  if (veranstaltung.onlineTool !== null) {
    return undefined;
  }

  const location = getLocation(veranstaltung);
  return location?.name ?? undefined;
};

type GetLocationKuerzelVa = {
  onlineTool: CservOnlineTools | null;
  standortBlockungen: {
    standort: {
      kuerzel: string;
    };
  }[];
  raumBlockungen: {
    raum: {
      location: {
        kuerzel: string;
      };
    };
  }[];
};

export const getLocationKuerzel = (veranstaltung: GetLocationKuerzelVa): string | undefined => {
  if (veranstaltung.onlineTool === cservOnlineTools.ZOOM) {
    return 'Zoom';
  }

  if (veranstaltung.onlineTool !== null) {
    return undefined;
  }

  return getLocation(veranstaltung)?.kuerzel;
};

export const getLocationArt = (veranstaltung: GetLocationVa<{ art: LocationArt | null }>) =>
  getLocation(veranstaltung)?.art ?? undefined;

type GetStandortNameVa = {
  standortBlockungen: {
    standort: {
      name: string;
    };
  }[];
  raumBlockungen: {
    raum: {
      location: {
        standort: {
          name: string;
        } | null;
      };
    };
  }[];
};

export const getStandortName = (veranstaltung: GetStandortNameVa): string | undefined => {
  const standort = getStandort(veranstaltung);
  const location = getLocation(veranstaltung);

  return location?.standort?.name ?? standort?.name ?? undefined;
};

type GetStandortKuerzelVa = {
  standortBlockungen: {
    standort: {
      kuerzel: string;
    };
  }[];
  raumBlockungen: {
    raum: {
      location: {
        standort: {
          kuerzel: string;
        } | null;
      };
    };
  }[];
};

export const getStandortKuerzel = (veranstaltung: GetStandortKuerzelVa): string | undefined => {
  const standort = getStandort(veranstaltung);
  const location = getLocation(veranstaltung);

  return location?.standort?.kuerzel ?? standort?.kuerzel ?? undefined;
};

type GetRegionNameVa = {
  onlineTool: CservOnlineTools | null;
  standortBlockungen: {
    standort: {
      region: {
        name: string;
      } | null;
    };
  }[];
  raumBlockungen: {
    raum: {
      location: {
        region: {
          name: string;
        } | null;
      };
    };
  }[];
};

export const getRegionName = (veranstaltung: GetRegionNameVa): string | undefined => {
  if (veranstaltung.onlineTool !== null) {
    return 'Online';
  }

  const standort = getStandort(veranstaltung);
  const location = getLocation(veranstaltung);

  return location?.region?.name ?? standort?.region?.name ?? undefined;
};

export function getMultiExperteName(experten: ExperteNameWithGesperrtStatus[]): string | undefined {
  const names = experten
    .map((experte) => `${experte.vorname} ${experte.nachname}`)
    .filter(unique)
    .toSorted();

  if (names.length > 0) {
    return names.join(', ');
  } else {
    return undefined;
  }
}

type GetExperteNamesVa = {
  experteBlockungen: {
    experteSapId: number;
    experte: ExperteNameWithGesperrtStatus;
  }[];
};

export const getExperteNamesFromVeranstaltung = (veranstaltung: GetExperteNamesVa): string | undefined =>
  getExperteNames(
    veranstaltung.experteBlockungen.map((item) => ({
      experteSapId: item.experteSapId,
      ...item.experte,
    })),
  );

export function getExperteNames(
  experten: { experteSapId: number; vorname: string; nachname: string; istGesperrt?: boolean }[],
): string | undefined {
  const expertenNames = experten
    .filter(uniqueBy('experteSapId'))
    .toSorted((a, b) => a.vorname.localeCompare(b.vorname))
    .map((item) => getExperteName({ istGesperrt: false, ...item }))
    .join(', ');

  return expertenNames.length === 0 ? undefined : expertenNames;
}

export const getExperteName = (experte?: ExperteNameWithGesperrtStatus): string => {
  if (!experte) {
    return '';
  }
  if (experte.istGesperrt) {
    return `(gesperrt) ${experte.vorname} ${experte.nachname}`;
  }

  return `${experte.vorname} ${experte.nachname}`;
};

type GetOnlineToolNameVa = {
  onlineTool: CservOnlineTools | null;
};

export const getOnlineToolName = (veranstaltung: GetOnlineToolNameVa): string | undefined => {
  if (veranstaltung.onlineTool === null) {
    return undefined;
  }

  return content.tools[veranstaltung.onlineTool];
};

export const getExperteFullAnrede = (experte: SelectedExperte): string =>
  `${experte.anrede}${experte.titel ? ` ${experte.titel}` : ''}`;
